export interface ResolveCheckoutGrossTotalInput {
	subtotal: number;
	shipping: number;
	tax?: number;
	discount: number;
	apiTotal: number;
	/** Sum of (undiscounted line total − line total) when available from checkout lines. */
	lineDiscountInPrices?: number;
	/** Max absolute difference treated as equal (default 0.02). */
	epsilon?: number;
}

const DEFAULT_EPSILON = 0.02;

function approxEqual(a: number, b: number, epsilon: number): boolean {
	return Math.abs(a - b) <= epsilon;
}

/**
 * Resolves the checkout gross total to display and charge.
 *
 * When Saleor distributes the voucher into line prices, `totalPrice` is already correct.
 * When `discount` is set but line prices are unchanged, `totalPrice` may omit the voucher —
 * subtract `discount` only in that case.
 */
export function resolveCheckoutGrossTotal(input: ResolveCheckoutGrossTotalInput): number {
	const {
		subtotal,
		shipping,
		tax = 0,
		discount,
		apiTotal,
		lineDiscountInPrices,
		epsilon = DEFAULT_EPSILON,
	} = input;

	if (discount <= 0) {
		return apiTotal;
	}

	if (lineDiscountInPrices !== undefined && lineDiscountInPrices >= discount - epsilon) {
		return apiTotal;
	}

	const sumWithoutDiscount = subtotal + shipping + tax;
	if (approxEqual(apiTotal, sumWithoutDiscount, epsilon)) {
		return Math.max(0, apiTotal - discount);
	}

	return apiTotal;
}

type LinePriceFields = {
	variant?: {
		id?: string;
		pricing?: {
			price?: { net?: { amount?: number | null } | null; gross?: { amount?: number | null } | null } | null;
		} | null;
	} | null;
	quantity: number;
	totalPrice?: { gross?: { amount?: number | null } | null; net?: { amount?: number | null } | null } | null;
	undiscountedUnitPrice?: { gross?: { amount?: number | null } | null } | { amount?: number | null } | null;
};

/**
 * Which basis `CheckoutLine.undiscountedUnitPrice` is denominated in — the load-bearing
 * assumption of this whole module.
 *
 * `undiscountedUnitPrice` is the channel base price or the app price override. Unlike the order-line
 * equivalent it is a plain `MoneyField`, not a `TaxedMoneyField`, and the checkout tax
 * layer never rewrites it. Its basis therefore simply follows the channel's
 * `prices_entered_with_tax` flag: NET when false (our config), GROSS when true.
 *
 * Everything below compares it against the matching side of `totalPrice`. Comparing it
 * against the other basis silently underflows to 0 once VAT is non-zero, which reads as
 * "no voucher in the line prices" and makes callers subtract the voucher a second time.
 *
 * If `prices_entered_with_tax` is ever flipped to true in the Saleor channel config,
 * flip this to "gross" in the same change.
 */
const LINE_PRICE_BASIS: "net" | "gross" = "net";

function undiscountedUnitAmount(undiscountedUnitPrice: LinePriceFields["undiscountedUnitPrice"]): number {
	if (!undiscountedUnitPrice) {
		return 0;
	}
	if ("gross" in undiscountedUnitPrice && undiscountedUnitPrice.gross?.amount != null) {
		return undiscountedUnitPrice.gross.amount;
	}
	if ("amount" in undiscountedUnitPrice && undiscountedUnitPrice.amount != null) {
		return undiscountedUnitPrice.amount;
	}
	return 0;
}

/**
 * Charged line total on the same basis as `undiscountedUnitPrice` (see LINE_PRICE_BASIS).
 * Falls back to gross so this stays a no-op while tax is still 0 everywhere and net == gross.
 */
function lineTotalOnPriceBasis(line: LinePriceFields): number {
	const basisAmount =
		LINE_PRICE_BASIS === "net" ? line.totalPrice?.net?.amount : line.totalPrice?.gross?.amount;
	return basisAmount ?? line.totalPrice?.gross?.amount ?? 0;
}

/**
 * Sum voucher discount already distributed into checkout line prices.
 * Both operands are on LINE_PRICE_BASIS — mixing net and gross here breaks the detector.
 */
export function sumCheckoutLineDiscountInPrices(lines: LinePriceFields[]): number {
	return lines.reduce((sum, line) => {
		const undiscounted = lineUndiscountedTotal(line);
		return sum + Math.max(0, undiscounted - lineTotalOnPriceBasis(line));
	}, 0);
}

/**
 * Line total at undiscounted unit price, on LINE_PRICE_BASIS (falls back to charged line total).
 *
 * This feeds the *displayed* subtotal and line rows. The checkout breakdown renders `tax` as
 * its own row (see order-summary.tsx), and its callers already pass a NET subtotal/shipping,
 * so line rows must be net too for the column to add up to the total.
 */
function lineUndiscountedTotal(line: LinePriceFields): number {
	const channel = line.variant?.pricing?.price;
	const channelAmount = channel?.net?.amount ?? channel?.gross?.amount;
	const overrideOrBase = undiscountedUnitAmount(line.undiscountedUnitPrice);
	const unit =
		overrideOrBase > 0 && typeof channelAmount === "number"
			? Math.min(overrideOrBase, channelAmount)
			: overrideOrBase > 0
				? overrideOrBase
				: channelAmount;
	return typeof unit === "number" ? unit * line.quantity : lineTotalOnPriceBasis(line);
}

export interface CheckoutDisplayAmountsInput {
	lines: LinePriceFields[];
	apiSubtotal: number;
	shipping: number;
	tax: number;
	discount: number;
	apiTotal: number;
}

export function resolveCheckoutDisplayAmounts(input: CheckoutDisplayAmountsInput): {
	subtotal: number;
	total: number;
	lineAmounts: number[];
} {
	const lineDiscountInPrices = sumCheckoutLineDiscountInPrices(input.lines);
	const charged = input.lines.map(lineTotalOnPriceBasis);
	// Saleor can fold all or only part of a voucher into line prices. Restore
	// that portion for the separate voucher row, retaining catalogue/customer prices.
	const outsideLines = Math.max(0, input.apiSubtotal + input.shipping + input.tax - input.apiTotal);
	const folded = Math.max(0, Math.min(input.discount - outsideLines, input.discount, lineDiscountInPrices));
	const deltas = input.lines.map((line, index) => Math.max(0, lineUndiscountedTotal(line) - charged[index]));
	const sumDelta = deltas.reduce((sum, value) => sum + value, 0);
	const foldedCents = Math.round(folded * 100);
	let allocated = 0;
	const lineAmounts = charged.map((amount, index) => {
		const cumulative = deltas.slice(0, index + 1).reduce((sum, value) => sum + value, 0);
		const next = sumDelta > 0 ? Math.round((foldedCents * cumulative) / sumDelta) : 0;
		const share = next - allocated;
		allocated = next;
		return Math.round(amount * 100 + share) / 100;
	});
	return {
		subtotal: Math.round((input.apiSubtotal + folded) * 100) / 100,
		total: resolveCheckoutGrossTotal({
			subtotal: input.apiSubtotal,
			shipping: input.shipping,
			tax: input.tax,
			discount: input.discount,
			apiTotal: input.apiTotal,
			lineDiscountInPrices,
		}),
		lineAmounts,
	};
}
